import { useEffect, useState } from 'react';
import { ref } from 'firebase/database';
import { db } from '../../firebaseConfig';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { hasPaidAccess, hasRunningScoutAccess } from '../entitlements';
import { normalizeFeedPayload, normalizeRadarItems, normalizeAppliedJobs, normalizeDismissedJobs } from './opportunityData';

export function useOpportunityAccess() {
  const { entitlements, refresh } = useEntitlements();
  const [now, setNow] = useState(Date.now);
  const endsAt = Number(entitlements?.scout?.trialEndsAt || 0);
  useEffect(() => {
    setNow(Date.now());
    if (endsAt <= Date.now()) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(endsAt - Date.now() + 50, 2147483647));
    return () => clearTimeout(timer);
  }, [endsAt, entitlements]);
  const premium = hasRunningScoutAccess(entitlements, now);
  return { premium, trial: premium && !hasPaidAccess(entitlements) && Boolean(entitlements?.scout?.trialActive), endsAt, refresh };
}

function useCachedRecord(path) {
  const [record, setRecord] = useState(() => ({ path, value: getCachedFirebaseValue(path), error: false }));
  useEffect(() => {
    if (!path) { setRecord({ path, value: {}, error: false }); return undefined; }
    setRecord({ path, value: getCachedFirebaseValue(path), error: false });
    return subscribeCachedValue(ref(db, path), path,
      (snapshot) => setRecord({ path, value: snapshot.val() || {}, error: false }),
      () => setRecord({ path, value: {}, error: true }));
  }, [path]);
  const current = record.path === path ? record : { value: getCachedFirebaseValue(path), error: false };
  return { value: current.value || {}, loaded: current.value !== undefined, error: current.error };
}

export default function useOpportunityData(user, profile, language = 'en') {
  const root = user?.uid ? `people/${user.uid}` : '';
  const scoutId = profile?.primaryScoutId || '';
  const feeds = useCachedRecord(root && `${root}/jobScoutFeeds`);
  const applications = useCachedRecord(root && `${root}/application_index/applications`);
  const feedback = useCachedRecord(root && `${root}/jobScoutFeedback`);
  const radar = useCachedRecord(root && `${root}/companyRadar`);
  const processing = useCachedRecord(root && scoutId && `${root}/jobScouts/${scoutId}/processing`);
  const appliedJobIds = normalizeAppliedJobs(applications.value);
  const dismissed = normalizeDismissedJobs(feedback.value);
  const matches = normalizeFeedPayload(feeds.value, scoutId, language).map((job) => ({ ...job, isMatch: true }));
  return {
    matches, visibleMatches: matches.filter((job) => !appliedJobIds.has(job.id) && !dismissed.has(job.id)),
    applications: applications.value, appliedJobIds, radar: normalizeRadarItems(radar.value), processing: processing.value,
    loaded: feeds.loaded && applications.loaded && feedback.loaded, error: feeds.error || applications.error,
  };
}
